import { Component } from '@theme/component';

/**
 * Three-up portrait video showcase carousel. Scrolls the track one card at a time and
 * manages the previous / next controls, hiding them when the row fits without scrolling.
 *
 * @typedef {object} VideoShowcaseRefs
 * @property {HTMLElement} [track] - The horizontally scrollable track
 * @property {HTMLButtonElement} [prev] - The previous control
 * @property {HTMLButtonElement} [next] - The next control
 *
 * @extends {Component<VideoShowcaseRefs>}
 */
export class VideoShowcaseComponent extends Component {
  /** @type {HTMLElement | null} */
  #boundTrack = null;

  #onScroll = () => this.#update();

  #onResize = () => this.#update();

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
    requestAnimationFrame(() => this.#update());
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bind();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#boundTrack?.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onResize);
    this.#boundTrack = null;
  }

  /**
   * Scroll to the previous card.
   *
   * @param {Event} [event] - The triggering event
   */
  prev(event) {
    event?.preventDefault();
    this.#scrollByStep(-1);
  }

  /**
   * Scroll to the next card.
   *
   * @param {Event} [event] - The triggering event
   */
  next(event) {
    event?.preventDefault();
    this.#scrollByStep(1);
  }

  #bind() {
    const track = this.refs.track ?? null;
    if (this.#boundTrack !== track) {
      this.#boundTrack?.removeEventListener('scroll', this.#onScroll);
      this.#boundTrack = track;
      this.#boundTrack?.addEventListener('scroll', this.#onScroll, { passive: true });
    }
    window.removeEventListener('resize', this.#onResize);
    window.addEventListener('resize', this.#onResize);
  }

  #step() {
    const track = this.refs.track;
    if (!track) return 0;
    const first = track.firstElementChild;
    if (!first) return track.clientWidth;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  #scrollByStep(direction) {
    const track = this.refs.track;
    if (!track) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: direction * this.#step(), behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  #update() {
    const track = this.refs.track;
    if (!track) return;
    const { prev, next } = this.refs;
    const max = track.scrollWidth - track.clientWidth;
    const scrollable = max > 2;
    if (prev) {
      prev.hidden = !scrollable;
      prev.disabled = !scrollable || track.scrollLeft <= 2;
    }
    if (next) {
      next.hidden = !scrollable;
      next.disabled = !scrollable || track.scrollLeft >= max - 2;
    }
  }
}

if (!customElements.get('video-showcase')) {
  customElements.define('video-showcase', VideoShowcaseComponent);
}
